/**
 * External dependencies
 */
// Require Editor CSS files.
import 'froala-editor/css/froala_editor.pkgd.min.css';
// Require Editor JS files.
import 'froala-editor/js/froala_editor.pkgd.min.js';
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/char_counter.min.js';
import 'froala-editor/js/plugins/code_beautifier.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/draggable.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/line_height.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/paragraph_style.min.js';
import 'froala-editor/js/plugins/quick_insert.min.js';
import 'froala-editor/js/plugins/quote.min.js';
import 'froala-editor/js/plugins/table.min.js';
// Base para los textos de Froala que no traduce i18n (popups): ver textEditorLanguage.ts.
import 'froala-editor/js/languages/es.js';
import 'froala-editor/js/languages/pt_br.js';

import { MATH_RENDER_ENGINE_TANGERINE } from 'app/config/config';
import clsx from 'clsx';
import { addLatex } from 'core/utils/katex/config';
import FroalaEditor from 'froala-editor';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import FroalaEditorInput from 'react-froala-wysiwyg';
import { withTranslation } from 'react-i18next';
import { FROALA3_LECENSE_KEY, getToolbarVariant, toolbarButtons } from '../../../utils/froalaConfig';
import './froalaNumGroupButton';
import './froalaPluginMathV3.js';

import { applyEditorAccessibilityAttributes } from './editorAccessibility';
import { translateToolbarButtons } from './froalaLanguage';
import { registerTextEditorLanguage } from './textEditorLanguage';
import useMathLive from './useMathLive';

const TextEditor = ({
  t,
  i18n = undefined,
  id = undefined,
  ariaLabel = '',
  ariaLabelledBy = undefined,
  'aria-describedby': ariaDescribedBy = undefined,
  'aria-invalid': ariaInvalid = undefined,
  'aria-required': ariaRequired = undefined,
  placeholder = t('Write here'),
  onModelChange = () => null,
  model = '',
  extraConfig = {},
  disabled = false,
}) => {
  useMathLive();

  // El elemento editable vive en estado (no en una ref) porque Froala lo crea DESPUÉS
  // del montaje, en su callback `initialized`: un efecto que dependiera de una ref no
  // se volvería a ejecutar cuando por fin existe.
  const [editorElement, setEditorElement] = useState(null);
  const [editorInstance, setEditorInstance] = useState(null);

  useEffect(() => {
    addLatex(MATH_RENDER_ENGINE_TANGERINE);
  }, []);

  const interfaceLanguage = i18n?.language;
  // Sin `language`, Froala anuncia la barra en inglés con la interfaz en cualquier idioma (TAN-11367).
  const froalaLanguage = useMemo(() => registerTextEditorLanguage(FroalaEditor.LANGUAGE, interfaceLanguage, t), [interfaceLanguage, t]);
  // En el render y sobre el mismo pack: Froala lo lee al montar el hijo, antes de los efectos (TAN-10988).
  translateToolbarButtons(t, froalaLanguage);

  // Config derivado con useMemo (no useState + useEffect): un default `extraConfig = {}`
  // crea un objeto nuevo en cada render, y un efecto que hace setConfig con esa
  // dependencia entra en bucle infinito de re-renders. Froala solo lee config al montar.
  const config = useMemo(() => ({
    key: FROALA3_LECENSE_KEY,
    events: {
      'froalaEditor.initialized': function () {},
      'froalaEditor.focus': function () {},
      'froalaEditor.paste.beforeCleanup': function () {},
      initialized() {
        const editor = this;
        setEditorInstance(editor);
        setEditorElement(editor.el ?? null);
        disabled && editor.edit.off();
      },
    },
    fontSize: ['12', '13', '14', '15', '16', '17', '18', '20', '22', '24', '26', '28', '30', '32', '34', '36', '38', '40', '42', '44', '46', '48', '50', '52'],
    immediateReactModelUpdate: true,
    keepFormatOnDelete: true,
    language: froalaLanguage,
    // `lineBreakerTags` define junto a qué elementos (tablas, imágenes, hr...)
    // Froala muestra su línea para insertar un párrafo. Usamos una clase que no
    // existe en el contenido para que esa línea NO aparezca nunca (mantenemos el
    // comportamiento que había). No se puede usar `[]`: Froala hace
    // `lineBreakerTags.join(',')`, y con el array vacío el selector queda como ''
    // y `element.matches('')` lanza un SyntaxError que rompe el editor. (TAN-8789)
    lineBreakerTags: ['.no-line-breaker'],
    linkAlwaysBlank: true,
    listAdvancedTypes: true,
    multiLine: true,
    pastePlain: true,
    placeholderText: placeholder,
    quickInsertEnabled: true,
    quickInsertTags: ['p', 'div'],
    tabSpaces: 4,
    toolbarButtons: toolbarButtons,
    toolbarButtonsMD: toolbarButtons,
    toolbarButtonsSM: toolbarButtons,
    toolbarButtonsXS: toolbarButtons,
    toolbarInline: disabled,
    toolbarVisibleWithoutSelection: true,
    wordPasteModal: false,
    htmlAllowedEmptyTags: ['.MathJax_SVG svg *'],
    ...extraConfig,
  }), [disabled, extraConfig, froalaLanguage, placeholder]);

  const toolbarVariant = getToolbarVariant(config.toolbarButtons);

  useEffect(() => {
    if (!editorInstance) return;

    if (disabled) {
      editorInstance.edit.off();
    } else {
      editorInstance.edit.on();
    }
  }, [disabled, editorInstance]);

  // Froala renderiza su propio `contenteditable` y no reenvía nada de lo que FormField
  // clona en su hijo (`id`, `aria-describedby`, `aria-invalid`, `aria-required`), así que
  // el `<label for>` apuntaba a un id inexistente y los mensajes de ayuda/error no se
  // anunciaban. Lo aplicamos aquí, y no en el config de Froala, porque Froala solo lee su
  // config al montar: así un cambio de idioma o de estado de validación sí se refleja.
  useEffect(() => {
    applyEditorAccessibilityAttributes(editorElement, { ariaDescribedBy, ariaInvalid, ariaLabel, ariaLabelledBy, ariaRequired, id });
  }, [ariaDescribedBy, ariaInvalid, ariaLabel, ariaLabelledBy, ariaRequired, editorElement, id]);

  return (
    <div className={clsx('text-editor__container', toolbarVariant && `text-editor__container--${toolbarVariant}`)}>
      <FroalaEditorInput config={config} onModelChange={onModelChange} model={model} />
    </div>
  );
};

TextEditor.propTypes = {
  'aria-describedby': PropTypes.string,
  'aria-invalid': PropTypes.bool,
  'aria-required': PropTypes.bool,
  ariaLabel: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  disabled: PropTypes.bool,
  i18n: PropTypes.shape({ language: PropTypes.string }),
  id: PropTypes.string,
  extraConfig: PropTypes.object,
  model: PropTypes.string,
  onModelChange: PropTypes.func,
  placeholder: PropTypes.string,
  t: PropTypes.func.isRequired,
};

export default withTranslation(['placeholder'])(TextEditor);
